import React, { useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { motion } from "framer-motion";
import { CheckCircle2, Tag } from "lucide-react";
import Navbar from "@/components/campus/Navbar";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";

export default function Book() {
  const [params] = useSearchParams();
  const ambassador_id = params.get("ambassador");
  const college = params.get("college");
  const type = params.get("type");
  const base = type === "chat" ? 49 : 99;
  const [step, setStep] = useState(1);
  const [coupon, setCoupon] = useState("");
  const [applied, setApplied] = useState(null);
  const [loading, setLoading] = useState(false);
  const { user } = useAuth();
  const nav = useNavigate();

  const apply = async () => {
    if (!coupon.trim()) return;
    setLoading(true);
    try {
      const res = await api.validateCoupon({ code: coupon, college_name: college, service_type: type });
      if (res.valid) { setApplied(res); toast.success(`${coupon.toUpperCase()} applied — ₹${res.discount} off!`); }
      else { toast.error(res.error); setApplied(null); }
    } catch (e) { toast.error("Failed"); }
    finally { setLoading(false); }
  };

  const confirm = async () => {
    setLoading(true);
    try {
      const conv = await api.createConversation({ ambassador_id, college_name: college, type, coupon_code: applied?.code });
      toast.success("Booking confirmed!");
      setStep(3);
      setTimeout(()=> nav(type === "chat" ? `/chat/${conv.id}` : `/call/${conv.id}`), 1500);
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
    finally { setLoading(false); }
  };

  const finalAmt = applied ? applied.final_amount : base;

  if (!user) { nav("/auth?mode=login"); return null; }

  return (
    <div className="min-h-screen bg-[#0F172A] text-white">
      <Navbar/>
      <div className="pt-28 px-6 max-w-xl mx-auto pb-20">
        <div className="flex items-center gap-2 mb-6 text-sm text-slate-400">
          <span className={step>=1?"text-white":""}>Review</span>·
          <span className={step>=2?"text-white":""}>Coupon</span>·
          <span className={step>=3?"text-white":""}>Confirm</span>
        </div>

        {step <= 2 && (
          <motion.div initial={{opacity:0,y:20}} animate={{opacity:1,y:0}} className="glass rounded-2xl p-6 space-y-4">
            <h1 className="font-display text-2xl font-bold">{type === "chat" ? "Chat" : "Audio Call"}</h1>
            <div className="space-y-2 text-sm">
              <Row k="At" v={college}/>
              <Row k="Service" v={type === "chat" ? "Chat (unlimited messages)" : "10-minute audio call"}/>
              <Row k="Original Price" v={`₹${base}`}/>
              {applied && <Row k="Discount" v={`−₹${applied.discount}`}/>}
              <div className="flex justify-between font-display text-xl font-bold pt-2 border-t border-white/10"><span>Total</span><span className={finalAmt===0?"text-emerald-400":""}>{finalAmt===0?"FREE":`₹${finalAmt}`}</span></div>
            </div>

            <div className="pt-3 border-t border-white/10">
              <div className="text-xs text-slate-400 mb-2 flex items-center gap-2"><Tag size={12}/>Have a coupon?</div>
              <div className="flex gap-2">
                <input data-testid="coupon-input" value={coupon} onChange={e=>setCoupon(e.target.value.toUpperCase())} placeholder="FIRSTCOLLEGE / FIRSTCALL" className="flex-1 bg-[#1E293B] border border-white/10 rounded-lg px-3 py-2 text-sm uppercase"/>
                <button data-testid="apply-coupon" onClick={apply} disabled={loading || !coupon} className="px-4 rounded-lg bg-[#2563EB] hover:bg-blue-600 text-sm font-semibold disabled:opacity-50">Apply</button>
              </div>
              {applied && <div className="mt-2 text-xs text-emerald-400 flex items-center gap-1"><CheckCircle2 size={12}/>{applied.code} applied</div>}
            </div>

            <button data-testid="confirm-booking" onClick={confirm} disabled={loading} className="w-full py-3 rounded-full bg-[#F59E0B] text-[#0F172A] font-bold disabled:opacity-50">{loading?"Processing...":`Continue${finalAmt>0?` · Pay ₹${finalAmt} (Mock)`:""}`}</button>
            <p className="text-[10px] text-slate-500 text-center">v1: Payment is auto-success (Razorpay test mode mocked)</p>
          </motion.div>
        )}

        {step === 3 && (
          <motion.div initial={{scale:0.9,opacity:0}} animate={{scale:1,opacity:1}} className="glass rounded-2xl p-8 text-center">
            <motion.div initial={{scale:0}} animate={{scale:1}} transition={{type:"spring",delay:0.1}} className="w-20 h-20 mx-auto rounded-full bg-emerald-500/20 text-emerald-400 flex items-center justify-center text-4xl">✓</motion.div>
            <h2 className="font-display text-2xl font-bold mt-4">Booking Confirmed!</h2>
            <p className="text-slate-400 text-sm mt-2">Redirecting to your {type}...</p>
          </motion.div>
        )}
      </div>
    </div>
  );
}
const Row = ({k,v}) => <div className="flex justify-between gap-3"><span className="text-slate-400">{k}</span><span className="text-right">{v}</span></div>;
